// src/components/e-sign/ESignDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  TableSortLabel,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import VisibilityIcon from '@material-ui/icons/Visibility';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import PersonIcon from '@material-ui/icons/Person';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ScheduleIcon from '@material-ui/icons/Schedule';
import EmailIcon from '@material-ui/icons/Email';
import { useHistory } from 'react-router-dom';
import CircularProgress from '@material-ui/core/CircularProgress';
import notify from '../../utils/notifier';
import ESignService from '../../services/ESignService';
import { Dialog, DialogTitle, DialogContent } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Document as PdfDocument, Page } from 'react-pdf';
import GetAppIcon from '@material-ui/icons/GetApp';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 'bold',
  },
  createButton: {
    margin: theme.spacing(1),
    backgroundColor: '#051c2c',
    color: 'white',
    '&:hover': {
      backgroundColor: '#083656',
    },
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3)
  },
  filtersCard: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  filterField: {
    marginBottom: theme.spacing(2),
  },
  filterActions: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  statusChip: {
    margin: theme.spacing(0.5),
  },
  tableContainer: {
    maxHeight: 'calc(100vh - 400px)',
  },
  draft: {
    backgroundColor: theme.palette.grey[300],
  },
  sent: {
    backgroundColor: theme.palette.info.light,
  },
  completed: {
    backgroundColor: theme.palette.success.light,
  },
  expired: {
    backgroundColor: theme.palette.error.light,
  },
  partially_signed: {
    backgroundColor: theme.palette.warning.light,
  },
  noDocumentsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(5),
    minHeight: '300px',
  },
  pdfContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    padding: '0 16px',
    boxSizing: 'border-box',
    '& .react-pdf__Document': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      maxWidth: '800px',
    },
    '& .react-pdf__Page': {
      marginBottom: theme.spacing(0.1),
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      '&:last-child': {
        marginBottom: 0
      },
      '& canvas': {
        maxWidth: '100% !important',
        width: 'auto !important',
        height: 'auto !important',
        display: 'block'
      }
    }
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none',
  },
  // New styles for recipient details
  recipientCell: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  recipientsList: {
    maxWidth: '300px',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
  },
  recipientItem: {
    padding: theme.spacing(1),
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  recipientStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  expandedRow: {
    backgroundColor: theme.palette.action.hover,
  },
  recipientChip: {
    margin: theme.spacing(0.25),
    fontSize: '0.7rem',
    height: '20px',
  },
  signedChip: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  pendingChip: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  rejectedChip: {
    backgroundColor: '#f44336', // Bright red
    color: 'white',
  },
  viewedChip: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
  },
}));

// Utility function to format dates
function formatDate(dateString) {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  const day = date.getDate().toString().padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
}

// Utility function to format date for input
function formatDateForInput(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
}

export default function ESignDashboard() {
  const classes = useStyles();
  const history = useHistory();
  
  // Document state
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter and pagination state
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    createdDateFrom: '',
    createdDateTo: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // PDF dialog state
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);

  // New state for recipient details
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [documentDetails, setDocumentDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(new Set());

  // Fetch documents with filters
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {
        _page: page + 1, // Backend expects 1-based pagination
        _limit: rowsPerPage,
        _sort: sortBy,
        _order: sortOrder,
      };

      // Add filters
      if (filters.status) {
        params.status = filters.status;
      }
      if (filters.search) {
        params.q = filters.search;
      }
      if (filters.createdDateFrom) {
        params.createdDateFrom = filters.createdDateFrom;
      }
      if (filters.createdDateTo) {
        params.createdDateTo = filters.createdDateTo;
      }

      const response = await ESignService.getDocuments(params);
      
      if (response && response.data && response.data.success) {
        // Filter out draft documents as per original requirement
        const filteredDocuments = (response.data.documents || []).filter(doc => doc.status !== 'draft');
        setDocuments(filteredDocuments);
        setTotalCount(response.data.pagination?.totalCount || 0);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      notify({
        type: 'error',
        message: 'An error occurred while loading documents',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sortBy, sortOrder, filters]);

  // Fetch document details including recipients
  const fetchDocumentDetails = async (documentId) => {
    if (documentDetails[documentId] || loadingDetails.has(documentId)) {
      return; // Already loaded or loading
    }

    try {
      setLoadingDetails(prev => new Set(prev).add(documentId));
      
      const response = await ESignService.getDocument(documentId);
      
      if (response && response.data && response.data.success) {
        setDocumentDetails(prev => ({
          ...prev,
          [documentId]: response.data
        }));
      }
    } catch (error) {
      console.error('Error fetching document details:', error);
      notify({
        type: 'error',
        message: 'Failed to load document details',
      });
    } finally {
      setLoadingDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    let url;
    if (pdfBlob) {
      url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
    }
    return () => {
      if (url) {
        URL.revokeObjectURL(url);
        setPdfUrl(null);
      }
    };
  }, [pdfBlob]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      search: '',
      createdDateFrom: '',
      createdDateTo: ''
    });
    setPage(0);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(0);
  };

  const handleCreateNew = () => {
    history.push('/e-sign/create');
  };

  // Handle recipient row expansion
  const handleRecipientToggle = async (documentId) => {
    const newExpandedRows = new Set(expandedRows);
    
    if (expandedRows.has(documentId)) {
      newExpandedRows.delete(documentId);
    } else {
      newExpandedRows.add(documentId);
      // Fetch details when expanding
      await fetchDocumentDetails(documentId);
    }
    
    setExpandedRows(newExpandedRows);
  };

  const handleViewDocument = async (documentId) => {
    const document = documents.find(doc => doc.id === documentId);
    if (!document) {
      throw new Error('Document not found');
    }
    handleViewAnyDocument(document.fileIdentifier, ESignService.getDocumentFromStorage);
  };

  const handleViewSignedDocument = async (documentId) => {
    handleViewAnyDocument(documentId, ESignService.getSignedDocumentFromStorage, 'Failed to load signed document');
  };

  const handleViewAnyDocument = async (documentId, apiMethod, errorMsg = 'Failed to load document') => {
    try {
      setPdfLoading(true);
      setPdfDialogOpen(true);

      const response = await apiMethod(documentId);

      if (response && response.data) {
        setPdfBlob(response.data);
      } else {
        throw new Error(errorMsg);
      }
    } catch (error) {
      notify({
        type: 'error',
        message: error.message || errorMsg,
      });
      setPdfDialogOpen(false);
    } finally {
      setPdfLoading(false);
    }
  };

  const getStatusChip = (status) => {
    let chipClass;
    let label;

    switch (status) {
      case 'draft':
        chipClass = classes.draft;
        label = 'Draft';
        break;
      case 'sent':
        chipClass = classes.sent;
        label = 'Sent';
        break;
      case 'completed':
        chipClass = classes.completed;
        label = 'Completed';
        break;
      case 'expired':
        chipClass = classes.expired;
        label = 'Expired';
        break;
      case 'partially_signed':
        chipClass = classes.partially_signed;
        label = 'Partially Signed';
        break;
      default:
        chipClass = '';
        label = status;
    }

    return (
      <Chip
        label={label}
        className={`${classes.statusChip} ${chipClass}`}
        size="small"
      />
    );
  };

  const getRecipientStatusChip = (status) => {
    let chipClass, label, icon;
    
    switch (status) {
      case 'signed':
        chipClass = classes.signedChip;
        label = 'Signed';
        icon = <CheckCircleIcon style={{ fontSize: '12px' }} />;
        break;
      case 'rejected':
        chipClass = classes.rejectedChip;
        label = 'Rejected';
        icon = <CloseIcon style={{ fontSize: '12px' }} />;
        break;
      case 'viewed':
        chipClass = classes.viewedChip;
        label = 'Viewed';
        icon = <VisibilityIcon style={{ fontSize: '12px' }} />;
        break;
      default:
        chipClass = classes.pendingChip;
        label = 'Pending';
        icon = <ScheduleIcon style={{ fontSize: '12px' }} />;
    }
    
    return (
      <Chip
        label={label}
        className={`${classes.recipientChip} ${chipClass}`}
        size="small"
        icon={icon}
      />
    );
  };

  const renderRecipientDetails = (documentId) => {
    const details = documentDetails[documentId];
    const isLoading = loadingDetails.has(documentId);

    if (isLoading) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" py={2}>
          <CircularProgress size={16} />
          <Typography variant="body2" style={{ marginLeft: 8 }}>
            Loading recipients...
          </Typography>
        </Box>
      );
    }

    if (!details || !details.recipients) {
      return (
        <Typography variant="body2" color="textSecondary">
          No recipient details available
        </Typography>
      );
    }

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Recipients ({details.recipients.length})
        </Typography>
        <List dense>
          {details.recipients.map((recipient, index) => (
            <ListItem key={recipient.id} divider={index < details.recipients.length - 1}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                      {recipient.name}
                    </Typography>
                    {getRecipientStatusChip(recipient.status)}
                  </Box>
                }
                secondary={
                  <Box>
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                      <EmailIcon fontSize="small" />
                      <Typography variant="caption">
                        {recipient.email}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      Order: {recipient.signingOrder}
                      {recipient.signedAt && (
                        <span> • Signed: {formatDate(recipient.signedAt)}</span>
                      )}
                      {recipient.status === 'rejected' && recipient.rejectionComment && (
                        <span> • Reason: {recipient.rejectionComment}</span>
                      )}
                      {recipient.status === 'rejected' && recipient.rejectedAt && (
                        <span> • Rejected: {formatDate(recipient.rejectedAt)}</span>
                      )}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  const getSortDirection = (field) => {
    return sortBy === field ? sortOrder : undefined;
  };

  return (
    <div className={classes.root}>
      <div className={classes.headerContainer}>
        <Typography variant="h5" className={classes.title}>
          E-Signature Documents
        </Typography>
        <Button
          variant="contained"
          className={classes.createButton}
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
        >
          Create New
        </Button>
      </div>

      {/* Filters Card */}
      <Card className={classes.filtersCard}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Search
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search Documents"
                variant="outlined"
                size="small"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by ID, Title, Status..."
                InputProps={{
                  startAdornment: <SearchIcon style={{ marginRight: 8, color: '#666' }} />
                }}
                className={classes.filterField}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined" size="small" className={classes.filterField}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="partially_signed">Partially Signed</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                variant="outlined"
                size="small"
                value={filters.createdDateFrom}
                onChange={(e) => handleFilterChange('createdDateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
                className={classes.filterField}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                variant="outlined"
                size="small"
                value={filters.createdDateTo}
                onChange={(e) => handleFilterChange('createdDateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
                className={classes.filterField}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <div className={classes.filterActions}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  size="small"
                >
                  Clear Filters
                </Button>
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper className={classes.paper}>
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
            <Typography variant="body2" style={{ marginLeft: 16 }}>
              Loading documents...
            </Typography>
          </div>
        ) : documents.length > 0 ? (
          <>
            <TableContainer className={classes.tableContainer}>
              <Table stickyHeader aria-label="e-sign documents table">
                <TableHead>
                  <TableRow>
                    <TableCell>S.No.</TableCell>
                    <TableCell>Document ID</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'title'}
                        direction={getSortDirection('title')}
                        onClick={() => handleSort('title')}
                        className={classes.sortableHeader}
                      >
                        Document Title
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'status'}
                        direction={getSortDirection('status')}
                        onClick={() => handleSort('status')}
                        className={classes.sortableHeader}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'created_at'}
                        direction={getSortDirection('created_at')}
                        onClick={() => handleSort('created_at')}
                        className={classes.sortableHeader}
                      >
                        Created Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Recipients</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell align="center">View</TableCell>
                    <TableCell align="center">View Signed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((document, index) => (
                    <React.Fragment key={document.id}>
                      <TableRow 
                        hover 
                        className={expandedRows.has(document.id) ? classes.expandedRow : ''}
                      >
                        <TableCell component="th" scope="row">
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell>{document.id}</TableCell>
                        <TableCell>{document.title}</TableCell>
                        <TableCell>{getStatusChip(document.status)}</TableCell>
                        <TableCell>{formatDate(document.createdAt)}</TableCell>
                        <TableCell>
                          <Box
                            display="flex"
                            alignItems="center"
                            className={classes.recipientCell}
                            onClick={() => handleRecipientToggle(document.id)}
                          >
                            <Typography variant="body2">
                              {document.recipientCount}
                            </Typography>
                            <IconButton size="small">
                              {expandedRows.has(document.id) ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>{document.completedCount} of {document.recipientCount}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Document">
                            <IconButton
                              aria-label="view"
                              onClick={() => handleViewDocument(document.id)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Signed Document">
                            <IconButton
                              aria-label="view signed"
                              onClick={() => handleViewSignedDocument(document.id)}
                            >
                              <AssignmentTurnedInIcon style={{ color: '#1976d2' }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {expandedRows.has(document.id) && (
                        <TableRow>
                          <TableCell colSpan={9} style={{ paddingBottom: 0, paddingTop: 0 }}>
                            <Collapse in={expandedRows.has(document.id)} timeout="auto" unmountOnExit>
                              <Box margin={1}>
                                {renderRecipientDetails(document.id)}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        ) : (
          <div className={classes.noDocumentsContainer}>
            <Typography variant="h6" gutterBottom>
              No documents found
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {Object.values(filters).some(f => f) 
                ? 'No documents match your current filters. Try adjusting your search criteria.'
                : 'Create your first e-signature document to get started'
              }
            </Typography>
            <Button
              variant="contained"
              className={classes.createButton}
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              style={{ marginTop: '16px' }}
            >
              Create New Document
            </Button>
          </div>
        )}
      </Paper>

      {/* PDF Dialog */}
      <Dialog
        open={pdfDialogOpen}
        onClose={() => setPdfDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          PDF Preview
          <IconButton
            aria-label="download"
            onClick={() => {
              if (pdfUrl) {
                const link = document.createElement('a');
                link.href = pdfUrl;
                link.download = 'document.pdf';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            }}
            style={{ position: 'absolute', right: 48, top: 8 }}
            disabled={!pdfUrl}
          >
            <GetAppIcon />
          </IconButton>
          <IconButton
            aria-label="close"
            onClick={() => setPdfDialogOpen(false)}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers style={{ minHeight: 500 }}>
          {pdfLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <CircularProgress />
              <span style={{ marginLeft: 16 }}>Loading PDF...</span>
            </div>
          ) : pdfBlob ? (
            <div className={classes.pdfContainer}>
              <PdfDocument
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={console.error}
                loading={
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <CircularProgress />
                    <span style={{ marginLeft: 16 }}>Loading PDF...</span>
                  </div>
                }
              >
                {numPages &&
                  Array.from(new Array(numPages), (el, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  ))
                }
              </PdfDocument>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <CircularProgress />
              <span style={{ marginLeft: 16 }}>Loading PDF...</span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}